<template>
  <div class="modal fade" id="entryDetailModal" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="mdi mdi-ticket-confirmation text-primary me-2"></i>
            Park Entry Details - {{ entry.ticketNumber }}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body">
          <!-- Entry Status Card -->
          <div class="card border mb-3" :class="getStatusCardClass()">
            <div class="card-body">
              <div class="row align-items-center">
                <div class="col-md-8">
                  <h5 class="mb-1">{{ entry.visitorName }}</h5>
                  <p class="mb-0">
                    <span class="badge" :class="getStatusBadge(entry.status)">{{ entry.status }}</span>
                    <span class="ms-2 text-muted">{{ getTimeInPark() }}</span>
                  </p>
                </div>
                <div class="col-md-4 text-md-end">
                  <h4 class="text-primary mb-0">{{ formatPrice(entry.totalFees) }}</h4>
                  <small class="text-muted">Total Amount</small>
                </div>
              </div>
            </div>
          </div>

          <!-- Entry Information Tabs -->
          <ul class="nav nav-tabs" role="tablist">
            <li class="nav-item">
              <a class="nav-link active" data-bs-toggle="tab" href="#visitor-info" role="tab">
                <i class="mdi mdi-account me-1"></i> Visitor Info
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" data-bs-toggle="tab" href="#vehicle-info" role="tab">
                <i class="mdi mdi-car me-1"></i> Vehicle Details
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" data-bs-toggle="tab" href="#payment-info" role="tab">
                <i class="mdi mdi-cash me-1"></i> Payment
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" data-bs-toggle="tab" href="#activity-log" role="tab">
                <i class="mdi mdi-history me-1"></i> Activity Log
              </a>
            </li>
          </ul>

          <div class="tab-content p-3">
            <!-- Visitor Information Tab -->
            <div class="tab-pane active" id="visitor-info" role="tabpanel">
              <div class="row">
                <div class="col-md-6">
                  <table class="table table-sm table-borderless">
                    <tbody>
                    <tr>
                      <td class="text-muted">Visitor Type:</td>
                      <td>
                          <span class="badge" :class="getEntryTypeBadge(entry.entryType)">
                            {{ entry.entryType }}
                          </span>
                      </td>
                    </tr>
                    <tr>
                      <td class="text-muted">ID/Passport:</td>
                      <td><strong>{{ entry.idNumber }}</strong></td>
                    </tr>
                    <tr>
                      <td class="text-muted">Phone Number:</td>
                      <td>{{ entry.phoneNumber }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">Email:</td>
                      <td>{{ entry.email || 'Not provided' }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">Country:</td>
                      <td>{{ getCountryName(entry.country) }}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
                <div class="col-md-6">
                  <table class="table table-sm table-borderless">
                    <tbody>
                    <tr>
                      <td class="text-muted">Entry Time:</td>
                      <td><strong>{{ formatDateTime(entry.entryTime) }}</strong></td>
                    </tr>
                    <tr>
                      <td class="text-muted">Entry Gate:</td>
                      <td>{{ entry.entryGate }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">Visitor Count:</td>
                      <td>
                        <strong>{{ entry.visitorCount }}</strong>
                        <span v-if="entry.adults" class="text-muted">
                            ({{ entry.adults }} adults, {{ entry.children || 0 }} children)
                          </span>
                      </td>
                    </tr>
                    <tr v-if="entry.status === 'EXITED'">
                      <td class="text-muted">Exit Time:</td>
                      <td><strong>{{ formatDateTime(entry.exitTime) }}</strong></td>
                    </tr>
                    <tr v-if="entry.status === 'EXITED'">
                      <td class="text-muted">Exit Gate:</td>
                      <td>{{ entry.exitGate }}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Special Activities -->
              <div v-if="hasSpecialActivities()" class="mt-3">
                <h6 class="text-primary">Special Activities</h6>
                <div class="row">
                  <div class="col-md-6" v-if="entry.activities?.photography">
                    <div class="alert alert-info py-2">
                      <i class="mdi mdi-camera me-2"></i>Professional Photography Permit
                    </div>
                  </div>
                  <div class="col-md-6" v-if="entry.activities?.filming">
                    <div class="alert alert-danger py-2">
                      <i class="mdi mdi-video me-2"></i>Commercial Filming Permit
                    </div>
                  </div>
                  <div class="col-md-6" v-if="entry.activities?.camping">
                    <div class="alert alert-success py-2">
                      <i class="mdi mdi-tent me-2"></i>Camping Permit ({{ entry.nights || 1 }} nights)
                    </div>
                  </div>
                  <div class="col-md-6" v-if="entry.activities?.research">
                    <div class="alert alert-warning py-2">
                      <i class="mdi mdi-flask me-2"></i>Research Permit
                    </div>
                  </div>
                </div>
              </div>

              <!-- Notes -->
              <div v-if="entry.notes" class="mt-3">
                <h6 class="text-primary">Additional Notes</h6>
                <div class="alert alert-secondary">
                  {{ entry.notes }}
                </div>
              </div>
            </div>

            <!-- Vehicle Information Tab -->
            <div class="tab-pane" id="vehicle-info" role="tabpanel">
              <div v-if="entry.vehicleReg">
                <div class="row">
                  <div class="col-md-6">
                    <table class="table table-sm table-borderless">
                      <tbody>
                      <tr>
                        <td class="text-muted">Registration:</td>
                        <td><strong class="font-size-16">{{ entry.vehicleReg }}</strong></td>
                      </tr>
                      <tr>
                        <td class="text-muted">Vehicle Type:</td>
                        <td>{{ entry.vehicleType }}</td>
                      </tr>
                      <tr>
                        <td class="text-muted">Driver Name:</td>
                        <td>{{ entry.driverName || 'Not specified' }}</td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                  <div class="col-md-6">
                    <table class="table table-sm table-borderless">
                      <tbody>
                      <tr>
                        <td class="text-muted">Vehicle Fee:</td>
                        <td><strong>{{ formatPrice(entry.vehicleFees) }}</strong></td>
                      </tr>
                      <tr>
                        <td class="text-muted">Entry Status:</td>
                        <td>
                            <span class="badge bg-success" v-if="entry.status === 'IN_PARK'">
                              Vehicle in Park
                            </span>
                          <span class="badge bg-secondary" v-else>
                              Vehicle Exited
                            </span>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Vehicle Image Placeholder -->
                <div class="text-center mt-3">
                  <div class="border rounded p-4 bg-light">
                    <i class="mdi mdi-car font-size-48 text-muted"></i>
                    <p class="text-muted mb-0">Vehicle Image</p>
                  </div>
                </div>
              </div>
              <div v-else class="text-center py-5">
                <i class="mdi mdi-car-off font-size-48 text-muted"></i>
                <h5 class="text-muted mt-3">No Vehicle Information</h5>
                <p class="text-muted">This entry does not have an associated vehicle</p>
              </div>
            </div>

            <!-- Payment Information Tab -->
            <div class="tab-pane" id="payment-info" role="tabpanel">
              <div class="row">
                <div class="col-md-8">
                  <h6 class="mb-3">Fee Breakdown</h6>
                  <table class="table table-sm">
                    <tbody>
                    <tr>
                      <td>Park Entry Fees</td>
                      <td class="text-end">{{ formatPrice(entry.parkFees) }}</td>
                    </tr>
                    <tr v-if="entry.vehicleFees > 0">
                      <td>Vehicle Entry Fees</td>
                      <td class="text-end">{{ formatPrice(entry.vehicleFees) }}</td>
                    </tr>
                    <tr class="table-active">
                      <td><strong>Total Amount</strong></td>
                      <td class="text-end"><strong class="text-primary">{{ formatPrice(entry.totalFees) }}</strong></td>
                    </tr>
                    </tbody>
                  </table>

                  <h6 class="mb-3 mt-4">Payment Details</h6>
                  <table class="table table-sm table-borderless">
                    <tbody>
                    <tr>
                      <td class="text-muted">Payment Method:</td>
                      <td>
                        <span class="badge bg-success">{{ entry.paymentMethod }}</span>
                      </td>
                    </tr>
                    <tr v-if="entry.mpesaCode">
                      <td class="text-muted">M-Pesa Code:</td>
                      <td><strong>{{ entry.mpesaCode }}</strong></td>
                    </tr>
                    <tr>
                      <td class="text-muted">Payment Status:</td>
                      <td>
                          <span class="badge bg-success">
                            <i class="mdi mdi-check-circle me-1"></i>Paid
                          </span>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
                <div class="col-md-4">
                  <div class="card bg-light">
                    <div class="card-body text-center">
                      <i class="mdi mdi-qrcode font-size-48"></i>
                      <p class="mb-0">QR Code</p>
                      <small class="text-muted">{{ entry.ticketNumber }}</small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Transaction History -->
              <div class="mt-4">
                <h6 class="mb-3">Transaction History</h6>
                <div class="table-responsive">
                  <table class="table table-sm">
                    <thead>
                    <tr>
                      <th>Date/Time</th>
                      <th>Transaction</th>
                      <th>Method</th>
                      <th>Amount</th>
                      <th>Status</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="payment in payments" :key="payment.id">
                      <td>{{ formatDateTime(payment.date) }}</td>
                      <td>{{ payment.reference }}</td>
                      <td>{{ payment.method }}</td>
                      <td>{{ formatPrice(payment.amount) }}</td>
                      <td>
                        <span class="badge bg-success">{{ payment.status }}</span>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- Activity Log Tab -->
            <div class="tab-pane" id="activity-log" role="tabpanel">
              <div class="timeline">
                <div class="timeline-item">
                  <div class="timeline-point">
                    <i class="mdi mdi-login bg-success text-white"></i>
                  </div>
                  <div class="timeline-content">
                    <h6 class="mb-1">Entry Recorded</h6>
                    <p class="text-muted mb-1">{{ formatDateTime(entry.entryTime) }}</p>
                    <p class="mb-0">Gate: {{ entry.entryGate }}</p>
                  </div>
                </div>

                <div v-if="entry.paymentMethod" class="timeline-item">
                  <div class="timeline-point">
                    <i class="mdi mdi-cash bg-primary text-white"></i>
                  </div>
                  <div class="timeline-content">
                    <h6 class="mb-1">Payment Received</h6>
                    <p class="text-muted mb-1">{{ formatDateTime(entry.entryTime) }}</p>
                    <p class="mb-0">{{ entry.paymentMethod }} - {{ formatPrice(entry.totalFees) }}</p>
                  </div>
                </div>

                <div v-if="entry.status === 'EXITED'" class="timeline-item">
                  <div class="timeline-point">
                    <i class="mdi mdi-logout bg-info text-white"></i>
                  </div>
                  <div class="timeline-content">
                    <h6 class="mb-1">Exit Recorded</h6>
                    <p class="text-muted mb-1">{{ formatDateTime(entry.exitTime) }}</p>
                    <p class="mb-0">Gate: {{ entry.exitGate }}</p>
                    <p class="mb-0">Duration: {{ entry.duration }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <div class="d-flex justify-content-between w-100">
            <div>
              <button v-if="entry.status === 'IN_PARK'" @click="recordExit()"
                      class="btn btn-warning me-2">
                <i class="mdi mdi-exit-run me-1"></i>Record Exit
              </button>
              <button @click="extendTime()" class="btn btn-info me-2"
                      v-if="entry.status === 'IN_PARK'">
                <i class="mdi mdi-clock-plus me-1"></i>Extend Time
              </button>
            </div>
            <div>
              <button @click="printTicket()" class="btn btn-primary me-2">
                <i class="mdi mdi-printer me-1"></i>Print
              </button>
              <button @click="sendSMS()" class="btn btn-success me-2">
                <i class="mdi mdi-message me-1"></i>SMS
              </button>
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import moment from 'moment';

export default {
  name: "EntryDetailModal",
  props: {
    entry: {
      type: Object,
      default: () => ({})
    },
    payments: {
      type: Array,
      default: () => []
    }
  },
  methods: {
    formatPrice(value) {
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 0
      }).format(value || 0);
    },

    formatDateTime(dateTime) {
      if (!dateTime) return 'N/A';
      return moment(dateTime).format('DD/MM/YYYY HH:mm');
    },

    getTimeInPark() {
      if (!this.entry.entryTime) return '';

      if (this.entry.status === 'EXITED' && this.entry.duration) {
        return `Duration: ${this.entry.duration}`;
      } else if (this.entry.status === 'IN_PARK') {
        const duration = moment.duration(moment().diff(moment(this.entry.entryTime)));
        const hours = Math.floor(duration.asHours());
        const minutes = duration.minutes();
        return `${hours}h ${minutes}m in park`;
      }
      return '';
    },

    getStatusCardClass() {
      const statusClasses = {
        'IN_PARK': 'border-success',
        'EXITED': 'border-secondary',
        'EXPIRED': 'border-danger',
        'VOIDED': 'border-warning'
      };
      return statusClasses[this.entry.status] || 'border-primary';
    },

    getStatusBadge(status) {
      const badges = {
        'IN_PARK': 'bg-success',
        'EXITED': 'bg-secondary',
        'EXPIRED': 'bg-danger',
        'VOIDED': 'bg-warning'
      };
      return badges[status] || 'bg-secondary';
    },

    getEntryTypeBadge(type) {
      const badges = {
        'CITIZEN': 'bg-success',
        'RESIDENT': 'bg-info',
        'NON_RESIDENT': 'bg-primary',
        'STUDENT': 'bg-warning',
        'VEHICLE': 'bg-secondary'
      };
      return badges[type] || 'bg-secondary';
    },

    getCountryName(code) {
      const countries = {
        'KE': 'Kenya',
        'TZ': 'Tanzania',
        'UG': 'Uganda',
        'RW': 'Rwanda',
        'US': 'United States',
        'GB': 'United Kingdom',
        'DE': 'Germany',
        'FR': 'France',
        'CN': 'China'
      };
      return countries[code] || code || 'Not specified';
    },

    hasSpecialActivities() {
      if (!this.entry.activities) return false;
      return Object.values(this.entry.activities).some(v => v === true);
    },

    recordExit() {
      this.$emit('record-exit', this.entry);
      // Close modal after emitting
      const modal = bootstrap.Modal.getInstance(document.getElementById('entryDetailModal'));
      modal.hide();
    },

    extendTime() {
      const hours = prompt('Extend entry by how many hours?', '2');
      if (hours && !isNaN(hours)) {
        this.$emit('extend-time', { entry: this.entry, hours: parseInt(hours) });
      }
    },

    printTicket() {
      this.$emit('print-ticket', this.entry.ticketNumber);
    },

    sendSMS() {
      this.$emit('send-sms', this.entry);
    }
  }
}
</script>

<style scoped>
.modal-dialog {
  max-width: 900px;
}

.nav-tabs .nav-link {
  color: #495057;
  border: 1px solid transparent;
}

.nav-tabs .nav-link.active {
  color: #0d6efd;
  background-color: #fff;
  border-color: #dee2e6 #dee2e6 #fff;
}

.table-borderless td {
  padding: 0.5rem 0;
}

.font-size-16 {
  font-size: 16px;
}

.font-size-48 {
  font-size: 48px;
}

/* Timeline Styles */
.timeline {
  position: relative;
  padding-left: 40px;
}

.timeline::before {
  content: '';
  position: absolute;
  left: 15px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #e9ecef;
}

.timeline-item {
  position: relative;
  padding-bottom: 30px;
}

.timeline-point {
  position: absolute;
  left: -25px;
  top: 0;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
}

.timeline-point i {
  font-size: 14px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.timeline-content {
  padding-left: 10px;
}

.timeline-content h6 {
  font-size: 14px;
  font-weight: 600;
}

.timeline-content p {
  font-size: 13px;
}

.alert {
  margin-bottom: 0.5rem;
}
</style>